// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, FormActions, Input, Skeleton } from '@/components/ui'
import { useRoomType, useUpdateRoomType } from '@/hooks/useRoomTypes'
import { useProperty } from '@/hooks/useProperties'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'

interface RoomTypeFormData {
  name: string
  floor: string
}

export function RoomTypeFormPage() {
  const { t } = useTranslation('units')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const roomTypeId = Number(id)
  const translateError = useApiErrorTranslator('units')
  const addToast = useNotificationStore((s) => s.addToast)

  const { data: roomType, isLoading } = useRoomType(roomTypeId)
  useProperty(roomType?.property_id || 0)
  const updateRoomType = useUpdateRoomType()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RoomTypeFormData>()

  useEffect(() => {
    if (roomType) {
      reset({
        name: roomType.name,
        floor: roomType.floor || '',
      })
    }
  }, [roomType, reset])

  const onSubmit = async (data: RoomTypeFormData) => {
    if (!roomType) return
    try {
      await updateRoomType.mutateAsync({
        id: roomTypeId,
        propertyId: roomType.property_id,
        name: data.name,
        floor: data.floor || undefined,
      })
      navigate(`/room-types/${roomTypeId}`)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-32" />
          </Card>
        </div>
    )
  }

  if (!roomType) {
    return (
        <Card>
          <p className="text-center py-8">{t('room_type.not_found')}</p>
        </Card>
    )
  }

  return (
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: tc('nav.properties'), href: '/properties' },
            { label: roomType.name, href: `/room-types/${roomTypeId}` },
            { label: tc('actions.edit') },
          ]}
        />

        <PageHeader title={`${t('room_type.edit')} ${roomType.name}`} description={t('room_type.edit_description')} />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Card title={t('room_type.detail')}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label={t('room_type.name')}
                placeholder={t('room_type.name_placeholder')}
                error={errors.name?.message}
                {...register('name', { required: t('validation.name_required') })}
              />
              <Input
                label={t('room_type.floor')}
                placeholder={t('room_type.floor_placeholder')}
                error={errors.floor?.message}
                {...register('floor')}
              />
            </div>
          </Card>

          <FormActions>
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              {tc('actions.cancel')}
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {tc('actions.save_changes')}
            </Button>
          </FormActions>
        </form>
      </div>
  )
}
